const SCENE_GRAPH_EXPORT_CSS = `
  svg, text, foreignObject, foreignObject * {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
  }
  .text-white { color: #fff; }
  .w-2 { width: 0.5rem; }
  .h-2 { height: 0.5rem; }
  .w-2\\.5 { width: 0.625rem; }
  .h-2\\.5 { height: 0.625rem; }
  .w-3 { width: 0.75rem; }
  .h-3 { height: 0.75rem; }
  .w-4 { width: 1rem; }
  .h-4 { height: 1rem; }
  .w-5 { width: 1.25rem; }
  .h-5 { height: 1.25rem; }
  .w-6 { width: 1.5rem; }
  .h-6 { height: 1.5rem; }
  .flex { display: flex; }
  .items-center { align-items: center; }
  .justify-center { justify-content: center; }
  .h-full { height: 100%; }
  .w-full { width: 100%; }
`;

const DEFAULT_EXPORT_BACKGROUND = "rgb(28, 28, 30)";
const APP_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';
const SVG_NS = "http://www.w3.org/2000/svg";
const XHTML_NS = "http://www.w3.org/1999/xhtml";

function timestampForFilename(date = new Date()) {
  return date.toISOString().replace(/[:.]/g, "-").replace("T", "_").replace("Z", "");
}

function getSvgPixelSize(svg) {
  const rect = svg.getBoundingClientRect?.();
  const widthAttr = Number(svg.getAttribute("width"));
  const heightAttr = Number(svg.getAttribute("height"));
  return {
    width: Math.max(1, Math.round(rect?.width || widthAttr || 1200)),
    height: Math.max(1, Math.round(rect?.height || heightAttr || 800)),
  };
}

function parseViewBox(viewBox) {
  const parts = String(viewBox || "")
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length !== 4 || parts.some((value) => !Number.isFinite(value))) return null;
  const [x, y, width, height] = parts;
  if (width <= 0 || height <= 0) return null;
  return { x, y, width, height };
}

function cloneSvgForExport(svg, { width, height, backgroundColor }) {
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", SVG_NS);
  clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  clone.setAttribute("style", `background: ${backgroundColor}; font-family: ${APP_FONT_FAMILY};`);
  clone.setAttribute("font-family", APP_FONT_FAMILY);

  const doc = svg.ownerDocument || document;
  normalizeForeignObjectNamespaces(clone);
  applyTextFontFamily(clone);

  const style = doc.createElementNS(SVG_NS, "style");
  style.textContent = SCENE_GRAPH_EXPORT_CSS;
  clone.insertBefore(style, clone.firstChild);

  const viewBox = parseViewBox(clone.getAttribute("viewBox"));
  if (viewBox) {
    const background = doc.createElementNS(SVG_NS, "rect");
    background.setAttribute("x", String(viewBox.x));
    background.setAttribute("y", String(viewBox.y));
    background.setAttribute("width", String(viewBox.width));
    background.setAttribute("height", String(viewBox.height));
    background.setAttribute("fill", backgroundColor);
    clone.insertBefore(background, style.nextSibling);
  }

  return clone;
}

function normalizeForeignObjectNamespaces(svg) {
  svg.querySelectorAll("foreignObject > *").forEach((element) => {
    element.setAttribute("xmlns", XHTML_NS);
    const existingStyle = element.getAttribute("style");
    element.setAttribute(
      "style",
      existingStyle
        ? `${existingStyle}; font-family: ${APP_FONT_FAMILY};`
        : `font-family: ${APP_FONT_FAMILY};`
    );
  });
  svg.querySelectorAll("foreignObject svg").forEach((element) => {
    element.setAttribute("xmlns", SVG_NS);
  });
}

function applyTextFontFamily(svg) {
  svg.querySelectorAll("text").forEach((text) => {
    text.setAttribute("font-family", APP_FONT_FAMILY);
  });
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function downloadSceneGraphSvg(svg, options = {}) {
  if (!svg) throw new Error("Scene Graph SVG is not mounted.");
  const { width, height } = getSvgPixelSize(svg);
  const backgroundColor = options.backgroundColor || DEFAULT_EXPORT_BACKGROUND;
  const filename = options.filename || `scene-graph_${timestampForFilename()}.svg`;
  const clone = cloneSvgForExport(svg, { width, height, backgroundColor });
  const svgText = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
  downloadBlob(filename, blob);
}
